/**
 * The notification control — an INDICATOR, not a count.
 *
 * The row asks for an unread *indicator*, and an indicator is not a number.
 * The design system reserves its count capsule for a static, non-interactive
 * figure, so pinning one to a control would both misuse the class and answer a
 * question nothing asked. The indicator is a dot, and because colour alone may
 * not carry meaning the control also holds a visually-hidden sentence saying
 * unread messages exist.
 *
 * Present at EVERY tier. It used to be two halves — a top-bar handle above the
 * tablet tier and an inbox entry in the bottom bar below it — but the bottom
 * bar carries catalogue destinations only, so nothing down there could take
 * over from it and the handle stays visible at all four tiers.
 *
 * On `/inbox` the dot stays LIT and the handle takes `aria-current="page"`.
 * Unread rows in the table below do not stop being unread because their
 * container is open; extinguishing the indicator there would state something
 * false.
 */
import { NavLink } from 'react-router-dom';

import { chromeCopy } from './copy';
import { IconBell } from './icons';
import { useUnreadCount } from './queries';

export function NotificationHandle() {
  const { data } = useUnreadCount();
  const hasUnread = (data?.unread_count ?? 0) > 0;

  return (
    <span className="relative inline-flex shrink-0">
      <NavLink
        to="/inbox"
        className="flex min-h-11 w-11 items-center justify-center rounded-sm text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text aria-[current=page]:text-accent-text"
      >
        <IconBell size={20} />
        <span className="visually-hidden">
          {hasUnread ? chromeCopy.notifications.unread : chromeCopy.notifications.label}
        </span>
      </NavLink>
      {hasUnread ? (
        <span
          aria-hidden="true"
          data-unread=""
          className="pointer-events-none absolute end-[0.45rem] top-[0.45rem] h-2 w-2 rounded-pill border border-bg bg-accent"
        />
      ) : null}
    </span>
  );
}
